// Logging utilities for Elasticsearch and Dynatrace

/* ---------- public surface ---------- */
export interface LogMessage {
	message: string // e.g. 'User {0} logged in from {1}'
	params?: unknown[] // values to interpolate
}

/** Sink for log delivery. Exported for testing. */
export interface LogSink {
	log(level: string, message: string): Promise<void>
	logBatch(
		entries: Array<{ level: string; message: string; timestamp: number }>,
	): Promise<void>
}

export class Logger {
	private systemInfo: SystemInfo | null = null
	private logBuffer: LogEntry[] = []
	private batchSize: number = 10
	private flushInterval: number = 5000 // 5 seconds
	private flushTimer: NodeJS.Timeout | null = null

	/** Test-only: create a Logger with a custom sink (no system info, no flush timer). */
	static createWithSink(sink: LogSink): Logger {
		return new Logger(sink as unknown as LogSinkInternal)
	}

	static async create(): Promise<Logger> {
		const cfg = await ManifestConfig.load()
		const logger = new Logger(SinkFactory.fromConfig(cfg))
		await logger.initializeSystemInfo()
		logger.startBatchFlush()
		return logger
	}

	info({ message, params = [] }: LogMessage) {
		const enrichedMessage = this.systemInfo
			? `${message} | Excel: ${this.systemInfo.excelVersion} | Browser: ${this.systemInfo.browserVersion} | OS: ${this.systemInfo.osVersion} | User: ${this.systemInfo.userName} | IP: ${this.systemInfo.ipAddress}`
			: message
		this.addToBuffer("INFO", format(enrichedMessage, params))
	}

	debug({ message, params = [] }: LogMessage) {
		const enrichedMessage = this.systemInfo
			? `${message} | Excel: ${this.systemInfo.excelVersion} | Browser: ${this.systemInfo.browserVersion} | OS: ${this.systemInfo.osVersion} | User: ${this.systemInfo.userName} | IP: ${this.systemInfo.ipAddress}`
			: message
		this.addToBuffer("DEBUG", format(enrichedMessage, params))
	}

	warn({ message, params = [] }: LogMessage) {
		const enrichedMessage = this.systemInfo
			? `${message} | Excel: ${this.systemInfo.excelVersion} | Browser: ${this.systemInfo.browserVersion} | OS: ${this.systemInfo.osVersion} | User: ${this.systemInfo.userName} | IP: ${this.systemInfo.ipAddress}`
			: message
		this.addToBuffer("WARN", format(enrichedMessage, params))
	}

	error({ message, params = [] }: LogMessage) {
		const enrichedMessage = this.systemInfo
			? `${message} | Excel: ${this.systemInfo.excelVersion} | Browser: ${this.systemInfo.browserVersion} | OS: ${this.systemInfo.osVersion} | User: ${this.systemInfo.userName} | IP: ${this.systemInfo.ipAddress}`
			: message
		this.addToBuffer("ERROR", format(enrichedMessage, params))
	}

	// Force flush all buffered logs
	async flush(): Promise<void> {
		if (this.logBuffer.length === 0) return

		const logsToSend = [...this.logBuffer]
		this.logBuffer = []

		try {
			await this.sink.logBatch(logsToSend)
		} catch (error) {
			console.error("Failed to flush log buffer:", error)
			// Fallback to individual log calls
			for (const entry of logsToSend) {
				await this.sink.log(entry.level, entry.message).catch(() => {})
			}
		}
	}

	private addToBuffer(level: LogLevel, message: string): void {
		this.logBuffer.push({
			level,
			message,
			timestamp: Date.now(),
		})

		// Flush if buffer reaches batch size
		if (this.logBuffer.length >= this.batchSize) {
			this.flush().catch(console.error)
		}
	}

	private startBatchFlush(): void {
		this.flushTimer = setInterval(() => {
			this.flush().catch(console.error)
		}, this.flushInterval)
	}

	// Cleanup method to be called when logger is no longer needed
	destroy(): void {
		if (this.flushTimer) {
			clearInterval(this.flushTimer)
			this.flushTimer = null
		}
		this.flush().catch(console.error)
	}

	private async initializeSystemInfo(): Promise<void> {
		try {
			this.systemInfo = {
				excelVersion: await this.getExcelVersion(),
				browserVersion: navigator.userAgent,
				osVersion: this.getOSVersion(),
				userName: await this.getUserName(),
				ipAddress: await this.getIPAddress(),
			}
		} catch (error) {
			console.warn("Failed to initialize system info:", error)
			this.systemInfo = null
		}
	}

	private async getExcelVersion(): Promise<string> {
		try {
			// First check if we already detected and cached the Excel version
			if (
				Office &&
				Office.context &&
				Office.context.document &&
				Office.context.document.settings
			) {
				const cachedVersion = Office.context.document.settings.get(
					"excel_edition",
				) as string
				if (cachedVersion) {
					return cachedVersion
				}
			}

			// Detect Excel edition and cache it
			let excelEdition = "Excel Desktop" // default
			let excelVersion = "Unknown"

			if (Office && Office.context) {
				console.log("Detecting Excel edition...")

				// Check for Excel Online indicators
				if ((Office.context.document as any).url) {
					const docUrl = (Office.context.document as any).url
					if (docUrl && (docUrl.includes("https") || docUrl.includes("http"))) {
						excelEdition = "Excel Online"
					}
				}

				// Combine edition and version
				const fullVersion = `${excelEdition} ${excelVersion}`

				// Cache the detected edition
				if (Office.context.document && Office.context.document.settings) {
					Office.context.document.settings.set("excel_edition", fullVersion)
					Office.context.document.settings.saveAsync()
					console.log("Cached Excel version:", fullVersion)
				}

				return fullVersion
			}

			return excelEdition
		} catch (error) {
			console.error("Error detecting Excel version:", error)
			return "Excel (Error)"
		}
	}

	private getOSVersion(): string {
		try {
			// Modern approach using userAgentData
			if (
				"userAgentData" in navigator &&
				(navigator as any).userAgentData?.platform
			) {
				return (navigator as any).userAgentData.platform
			}

			// Fallback using userAgent string parsing
			const userAgent = navigator.userAgent
			if (userAgent.includes("Windows")) return "Windows"
			if (userAgent.includes("Mac")) return "macOS"
			if (userAgent.includes("Linux")) return "Linux"
			if (userAgent.includes("Android")) return "Android"
			if (userAgent.includes("iOS")) return "iOS"

			return "Unknown OS"
		} catch (error) {
			return "Unknown OS"
		}
	}

	private async getIPAddress(): Promise<string> {
		try {
			const response = await fetch("https://api.ipify.org?format=json")
			const data = await response.json()
			return data.ip || "Unknown"
		} catch (error) {
			return "Unknown"
		}
	}

	private async getUserName(): Promise<string> {
		try {
			// Try to get user name from Office.js API
			if (Office && Office.context) {
				// For Excel Online, we can try to get user info from the document
				if (Office.context.document) {
					// Try to get from document properties or settings
					const userName = Office.context.document.settings.get(
						"user_name",
					) as string
					if (userName) return userName
				}

				// Try to get from Office context if available
				if ((Office.context as any).user?.displayName) {
					return (Office.context as any).user.displayName
				}
			}
			return "Unknown User"
		} catch (error) {
			return "Unknown User"
		}
	}

	private constructor(private readonly sink: LogSinkInternal) {}
}

/* ---------- internals below ---------- */
type LogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG"

interface LogEntry {
	level: LogLevel
	message: string
	timestamp: number
}

interface LogSinkInternal {
	log(lvl: LogLevel, msg: string): Promise<void>
	logBatch(entries: LogEntry[]): Promise<void>
}

/* Console (fallback) */
class ConsoleSink implements LogSinkInternal {
	async log(lvl: LogLevel, msg: string) {
		const time = new Date().toISOString()
		console.info(`[Console][${lvl}] ${time} ${msg}`)
	}

	async logBatch(entries: LogEntry[]): Promise<void> {
		for (const entry of entries) {
			await this.log(entry.level, entry.message)
		}
	}
}

/* ElasticSearch ND-JSON single-doc sink */
class ElasticSink implements LogSinkInternal {
	constructor(
		private url: string,
		private index: string,
		private apiKey: string,
	) {}

	async log(lvl: LogLevel, msg: string) {
		const doc = {
			timestamp: new Date().toISOString(),
			level: lvl,
			message: `[Elastic] ${msg}`,
		}
		const dateSuffix = new Date().toISOString().split("T")[0] // YYYY-MM-DD format
		const indexWithDate = `${this.index}-${dateSuffix}`

		await fetch(`${this.url}/${indexWithDate}/_doc`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `ApiKey ${this.apiKey}`,
			},
			body: JSON.stringify(doc),
		}).catch(() => {
			/* swallow */
		})
	}

	async logBatch(entries: LogEntry[]): Promise<void> {
		for (const entry of entries) {
			await this.log(entry.level, entry.message)
		}
	}
}

class DynatraceSink implements LogSinkInternal {
	constructor(
		private url: string,
		private token: string,
	) {}

	async log(lvl: LogLevel, msg: string) {
		const body = {
			logs: [
				{
					content: `[Dynatrace] ${msg}`,
					level: lvl,
					timestamp: new Date().toISOString(),
				},
			],
		}
		const urlWithDate = `${this.url}`

		await fetch(urlWithDate, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Api-Token ${this.token}`,
			},
			body: JSON.stringify(body),
		}).catch(() => {
			/* swallow */
		})
	}

	async logBatch(entries: LogEntry[]): Promise<void> {
		for (const entry of entries) {
			await this.log(entry.level, entry.message)
		}
	}
}

/* Tiny string formatter: 'Order {0}' → 'Order 42' */
function format(tpl: string, vals: unknown[]) {
	return tpl.replace(/\{(\d+)}/g, (_, i) => {
		const index = parseInt(i, 10)
		if (isNaN(index) || index < 0 || index >= vals.length) {
			return `{${i}}`
		}
		return String(vals[index] ?? `{${i}}`)
	})
}

/* Reads <bt:Property> values from manifest.xml */
class ManifestConfig {
	static async load() {
		try {
			const xml = await (await fetch("/manifest.xml")).text()
			return new DOMParser()
				.parseFromString(xml, "text/xml")
				.getElementsByTagName("bt:Property")
		} catch {
			/* network fail → empty list */
		}
		return [] as any
	}
	static get(propNodes: HTMLCollectionOf<Element>, name: string) {
		for (const el of Array.from(propNodes))
			if (el.getAttribute("Name") === name) return el.getAttribute("Value")
	}
}

/* Chooses the right sink */
class SinkFactory {
	static fromConfig(props: HTMLCollectionOf<Element>): LogSinkInternal {
		const p = ManifestConfig.get(props, "logger.provider")?.toLowerCase()
		switch (p) {
			case "elasticsearch": {
				const url = ManifestConfig.get(props, "elastic.endpoint")!
				const index = ManifestConfig.get(props, "elastic.index") ?? "logs"
				const apiKey = ManifestConfig.get(props, "elastic.apiKey") ?? ""
				return new ElasticSink(url, index, apiKey)
			}
			case "dynatrace": {
				const url = ManifestConfig.get(props, "dynatrace.endpoint")!
				const token = ManifestConfig.get(props, "dynatrace.token")!
				return new DynatraceSink(url, token)
			}
			default:
				return new ConsoleSink() // safe fallback
		}
	}
}

interface SystemInfo {
	excelVersion: string
	browserVersion: string
	osVersion: string
	userName: string
	ipAddress: string
}
